import { useState, useEffect, useMemo } from 'react';
import { format, startOfWeek, isBefore, isAfter } from 'date-fns';
import { CalendarView } from './useCalendarNavigation';
import { useCalendarNavigation } from './useCalendarNavigation';
import { useCalendarFilters } from './useCalendarFilters';
import { EventsByDate, CalendarEvent, getHorasPrevistasForDay } from '../utils/eventUtils';

export interface UseAgendaMobileReturn {
  // Navigation (also used by desktop Calendar)
  currentDate: Date;
  view: CalendarView;
  viewsOptions: { label: string; value: CalendarView }[];
  handleViewChange: (value: string | number | null) => void;
  goToToday: () => void;
  navigate: (direction: 'prev' | 'next') => void;
  navigateByDay: (direction: 'prev' | 'next') => void;
  weekDays: Date[];
  fetchAgendaTasks: (isWeekView?: boolean, overrides?: { planosValues?: string[] }) => Promise<void>;
  // Mobile-only: selected day within the week
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  // Data for selected day
  eventsForSelectedDay: CalendarEvent[];
  horasPrevistasSelectedDay: string;
  events: EventsByDate;
  isLoadingEvents: boolean;
  // Filters (same as web)
  type: 'pendentes' | 'baixadas';
  setType: (type: 'pendentes' | 'baixadas') => void;
  openFilters: boolean;
  setOpenFilters: (open: boolean) => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  planosSelectionLabel: string;
  executoresSetoresSelectionLabel: string;
  ambientesEquipamentosSelectionLabel: string;
  maintenanceUnitsOptions: { label: string; value: string }[];
  maintenanceUnitsValues: string[];
  handleMaintenanceUnitsChange: (values: unknown) => void;
  setExecutoresSetoresModalOpen: (open: boolean) => void;
  setAmbientesEquipamentosModalOpen: (open: boolean) => void;
  setPlanosModalOpen: (open: boolean) => void;
  // Modal state (for parent to render modals)
  executoresSetoresModalOpen: boolean;
  ambientesEquipamentosModalOpen: boolean;
  planosModalOpen: boolean;
  // Modal handlers
  executoresOptions: { label: string; value: string; foto?: string }[];
  tempExecutoresValues: string[];
  handleTempExecutoresChange: (values: unknown) => void;
  setoresOptions: { label: string; value: string }[];
  tempSetoresValues: string[];
  handleTempSetoresChange: (values: unknown) => void;
  applyExecutoresSetoresFilters: () => void;
  clearExecutoresSetoresFilters: () => void;
  onExecutoresSearch: (term: string) => void;
  areaGroupsOptions: { label: string; value: string }[];
  areaSubgroupsOptions: { label: string; value: string }[];
  areasOptions: { label: string; value: string }[];
  equipmentTypesOptions: { label: string; value: string }[];
  equipmentOptions: { label: string; value: string }[];
  tempAreaGroupsValues: string[];
  handleTempAreaGroupsChange: (values: unknown) => void;
  tempAreaSubgroupsValues: string[];
  handleTempAreaSubgroupsChange: (values: unknown) => void;
  tempAreasValues: string[];
  handleTempAreasChange: (values: unknown) => void;
  tempEquipmentTypesValues: string[];
  handleTempEquipmentTypesChange: (values: unknown) => void;
  tempEquipmentValues: string[];
  handleTempEquipmentChange: (values: unknown) => void;
  applyAmbientesEquipamentosFilters: () => void;
  clearAmbientesEquipamentosFilters: () => void;
  loadMoreAreas: () => Promise<void>;
  searchArea: (term: string) => void;
  loadMoreEquipamentos: () => Promise<void>;
  searchEquipamento: (term: string) => void;
  planosOptions: { label: string; value: string }[];
  tempPlanosValues: string[];
  handleTempPlanosChange: (values: unknown) => void;
  applyPlanosFilters: () => void;
  clearPlanosFilters: () => void;
}

const startOfWeekSunday = (d: Date) => startOfWeek(d, { weekStartsOn: 0 });
const endOfWeekSunday = (d: Date) => {
  const start = startOfWeekSunday(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
};

function isDateInWeek(date: Date, weekStart: Date): boolean {
  const start = startOfWeekSunday(weekStart);
  const end = endOfWeekSunday(weekStart);
  return !isBefore(date, start) && !isAfter(date, end);
}

export const useAgendaMobile = (): UseAgendaMobileReturn => {
  const navigation = useCalendarNavigation(undefined, CalendarView.Semana);
  const { currentDate, weekDays, navigate, view, viewsOptions, handleViewChange, goToToday, navigateByDay } =
    navigation;
  const filters = useCalendarFilters({ currentDate, view: CalendarView.Semana });

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  // When week changes, if selectedDate is outside the new week, reset to first day of week
  useEffect(() => {
    if (!isDateInWeek(selectedDate, currentDate)) {
      setSelectedDate(startOfWeekSunday(currentDate));
    }
  }, [currentDate, selectedDate]);

  const selectedDateKey = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);
  const eventsForSelectedDay = useMemo(() => filters.events[selectedDateKey] ?? [], [filters.events, selectedDateKey]);
  const horasPrevistasSelectedDay = useMemo(
    () => getHorasPrevistasForDay(eventsForSelectedDay),
    [eventsForSelectedDay],
  );

  return {
    currentDate,
    view,
    viewsOptions,
    handleViewChange,
    goToToday,
    navigate,
    navigateByDay,
    weekDays,
    fetchAgendaTasks: filters.fetchAgendaTasks,
    selectedDate,
    setSelectedDate,
    eventsForSelectedDay,
    horasPrevistasSelectedDay,
    events: filters.events,
    isLoadingEvents: filters.isLoadingEvents,
    type: filters.type,
    setType: filters.setType,
    openFilters: filters.openFilters,
    setOpenFilters: filters.setOpenFilters,
    hasActiveFilters: filters.hasActiveFilters,
    clearAllFilters: filters.clearAllFilters,
    planosSelectionLabel: filters.planosSelectionLabel,
    executoresSetoresSelectionLabel: filters.executoresSetoresSelectionLabel,
    ambientesEquipamentosSelectionLabel: filters.ambientesEquipamentosSelectionLabel,
    maintenanceUnitsOptions: filters.maintenanceUnitsOptions,
    maintenanceUnitsValues: filters.maintenanceUnitsValues,
    handleMaintenanceUnitsChange: filters.handleMaintenanceUnitsChange,
    setExecutoresSetoresModalOpen: filters.setExecutoresSetoresModalOpen,
    setAmbientesEquipamentosModalOpen: filters.setAmbientesEquipamentosModalOpen,
    setPlanosModalOpen: filters.setPlanosModalOpen,
    executoresSetoresModalOpen: filters.executoresSetoresModalOpen,
    ambientesEquipamentosModalOpen: filters.ambientesEquipamentosModalOpen,
    planosModalOpen: filters.planosModalOpen,
    executoresOptions: filters.executoresOptions,
    tempExecutoresValues: filters.tempExecutoresValues,
    handleTempExecutoresChange: filters.handleTempExecutoresChange,
    setoresOptions: filters.setoresOptions,
    tempSetoresValues: filters.tempSetoresValues,
    handleTempSetoresChange: filters.handleTempSetoresChange,
    applyExecutoresSetoresFilters: filters.applyExecutoresSetoresFilters,
    clearExecutoresSetoresFilters: filters.clearExecutoresSetoresFilters,
    onExecutoresSearch: filters.onExecutoresSearch,
    areaGroupsOptions: filters.areaGroupsOptions,
    areaSubgroupsOptions: filters.areaSubgroupsOptions,
    areasOptions: filters.areasOptions,
    equipmentTypesOptions: filters.equipmentTypesOptions,
    equipmentOptions: filters.equipmentOptions,
    tempAreaGroupsValues: filters.tempAreaGroupsValues,
    handleTempAreaGroupsChange: filters.handleTempAreaGroupsChange,
    tempAreaSubgroupsValues: filters.tempAreaSubgroupsValues,
    handleTempAreaSubgroupsChange: filters.handleTempAreaSubgroupsChange,
    tempAreasValues: filters.tempAreasValues,
    handleTempAreasChange: filters.handleTempAreasChange,
    tempEquipmentTypesValues: filters.tempEquipmentTypesValues,
    handleTempEquipmentTypesChange: filters.handleTempEquipmentTypesChange,
    tempEquipmentValues: filters.tempEquipmentValues,
    handleTempEquipmentChange: filters.handleTempEquipmentChange,
    applyAmbientesEquipamentosFilters: filters.applyAmbientesEquipamentosFilters,
    clearAmbientesEquipamentosFilters: filters.clearAmbientesEquipamentosFilters,
    loadMoreAreas: filters.loadMoreAreas,
    searchArea: filters.searchArea,
    loadMoreEquipamentos: filters.loadMoreEquipamentos,
    searchEquipamento: filters.searchEquipamento,
    planosOptions: filters.planosOptions,
    tempPlanosValues: filters.tempPlanosValues,
    handleTempPlanosChange: filters.handleTempPlanosChange,
    applyPlanosFilters: filters.applyPlanosFilters,
    clearPlanosFilters: filters.clearPlanosFilters,
  };
};
